/**
 * 360 panorama viewer of equirectangular images
 * @module
 *
 * iterated from https://github.com/mrdoob/three.js/blob/dd7bf582a/examples/webgl_panorama_equirectangular.html
 */
import { createFileRoute } from "@tanstack/react-router";
import type { PanoramaViewerScene } from "gfx/panoramaViewerTypes";
import Toggle from "lib/components/actions/Toggle";
import styles from "lib/components/debug/panoramaViewer.module.scss";
import GPURequiredContainer from "lib/components/gfx/GPURequiredContainer";
import usePanoramaViewer from "lib/components/walkthrough/usePanoramaViewer";
import getPageTitle from "lib/utils/getPageTitle";
import { useEffect, useRef, useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import { VIRTUAL_TILE_HORIZONTAL_COUNT, VIRTUAL_TILE_VERTICAL_COUNT } from "selections-compositor/state/selections";
import withAuthentication from "../../components/withAuthentication";

const startingTextureUrl = "/static/debug/POC_Epcon_Bathroom_360.png";
const startingScene = { textureUrl: startingTextureUrl };

function PanoramaViewerDebug() {
  const panoramaViewerContainerRef = useRef<HTMLDivElement>(null);

  const [scene, setScene] = useState<PanoramaViewerScene | undefined>(startingScene);

  const {
    animateCamera,
    setAnimateCamera,
    animationSpeed,
    setAnimationSpeed,
    resetCamera,
    resolveVisibleTiles,
    showDebugTile,
    toggleDebugVisibleTileAnchors,
    forceResize,
  } = usePanoramaViewer({ panoramaViewerContainerRef, scene, setScene });

  const [virtualHorizontalTileIndex, setVirtualHorizontalTileIndex] = useState<number | null>(null);
  const [virtualVerticalTileIndex, setVirtualVerticalTileIndex] = useState<number | null>(null);

  const [inDebugMode, setInDebugMode] = useState<boolean>(false);
  const [visibleTiles, setVisibleTiles] = useState<string>();

  const [imageName, setImageName] = useState<string | undefined>(startingTextureUrl.split("/").pop());

  const currentUserImageURL = useRef<string | undefined>(undefined);

  useEffect(() => {
    updateSlider();
  }, []);

  useEffect(() => {
    updateSlider();
  }, [animateCamera, animationSpeed]);

  useEffect(() => {
    forceResize();
  }, [inDebugMode]);

  function loadTexture() {
    const fileDialog = document.createElement("input");
    fileDialog.type = "file";

    fileDialog.onchange = (e: Event) => {
      const target = e.target as HTMLInputElement;

      if (!target.files || target.files.length === 0) {
        return;
      }

      const userImage = target.files[0];
      const userImageURL = URL.createObjectURL(userImage);

      setScene({ textureUrl: userImageURL });
      setImageName(userImage.name);

      if (currentUserImageURL.current !== undefined) {
        URL.revokeObjectURL(currentUserImageURL.current);
      }
      currentUserImageURL.current = userImageURL;
    };

    fileDialog.click();
  }

  function updateSlider() {
    const rangeInput: HTMLInputElement | null = document.querySelector("input[type=range]");
    if (!rangeInput) {
      return;
    }

    const rangeValue = rangeInput.valueAsNumber;
    const rangeMin = parseFloat(rangeInput.min);
    const rangeMax = parseFloat(rangeInput.max);
    const rangePercentage = (rangeValue - rangeMin) / (rangeMax - rangeMin);
    const percentageFromCenter = rangePercentage - 0.5;

    const rangeWidth = rangeInput.getBoundingClientRect().width;

    const rangeGauge: HTMLDivElement | null = document.querySelector(".rangeGauge");
    if (!rangeGauge) {
      return;
    }

    if (percentageFromCenter < 0) {
      rangeGauge.style.marginLeft = `${rangeWidth * rangePercentage}px`;
      rangeGauge.style.marginRight = "";
    } else {
      rangeGauge.style.marginLeft = `${rangeWidth * 0.5}px`;
      rangeGauge.style.marginRight = `${rangeWidth * rangePercentage}px`;
    }
    rangeGauge.style.width = `${rangeWidth * Math.abs(percentageFromCenter)}px`;
  }

  function cycleDebugTile() {
    let nextTileH, nextTileV;
    if (virtualHorizontalTileIndex === null || virtualVerticalTileIndex === null) {
      nextTileH = nextTileV = 0;
    } else {
      if (virtualHorizontalTileIndex === VIRTUAL_TILE_HORIZONTAL_COUNT - 1) {
        nextTileH = 0;
        nextTileV = (virtualVerticalTileIndex + 1) % VIRTUAL_TILE_VERTICAL_COUNT;
      } else {
        nextTileH = virtualHorizontalTileIndex + 1;
        nextTileV = virtualVerticalTileIndex;
      }
    }

    showDebugTile(nextTileH, nextTileV);
    setVirtualHorizontalTileIndex(nextTileH);
    setVirtualVerticalTileIndex(nextTileV);
  }

  function applyDebugTile() {
    setImageName("debug tiles");
    for (let i = 0; i < VIRTUAL_TILE_HORIZONTAL_COUNT; i++) {
      for (let j = 0; j < VIRTUAL_TILE_VERTICAL_COUNT; j++) {
        showDebugTile(i, j);
      }
    }
  }

  function toggleTileAnchors() {
    toggleDebugVisibleTileAnchors();
  }

  function printVisibleTiles() {
    setVisibleTiles(Array.from(resolveVisibleTiles()).sort().join(","));
  }

  return (
    <>
      <Head>
        <title>{getPageTitle("Panorama viewer")}</title>
      </Head>
      <div className={styles.panoramaViewerPage}>
        <div id="menuBar" className={styles.panoramaViewerMenuBar}>
          <button onClick={loadTexture}>Load texture</button>
          <button onClick={resetCamera}>Reset camera</button>

          <div className={styles.animateToggle}>
            <Toggle isTrue={animateCamera} onChange={() => setAnimateCamera(!animateCamera)} />
            <label>Animation</label>
          </div>
          {animateCamera && (
            <>
              <div className={styles.sliderWrapper}>
                <input
                  type="range"
                  min="-1.0"
                  max="1.0"
                  step="0.1"
                  value={animationSpeed}
                  onChange={(e) => setAnimationSpeed(e.currentTarget.valueAsNumber)}
                  onDoubleClick={() => setAnimationSpeed(0)}
                />
                <div className="rangeGaugeExtents" />
                <div className="rangeGauge" />
              </div>
              <label>Speed</label>
            </>
          )}
          <span>
            <b>Loaded:</b>&nbsp;{imageName}
          </span>

          <div className="pushRight">
            <input type="checkbox" onChange={() => setInDebugMode(!inDebugMode)} checked={inDebugMode} id="debugMode" />{" "}
            &nbsp;
            <label htmlFor="debugMode">Debug mode</label>
          </div>
        </div>
        {inDebugMode && (
          <div className={styles.panoramaViewerMenuBar}>
            <button onClick={cycleDebugTile}>
              Cycle debug tile{" "}
              {virtualHorizontalTileIndex !== null && virtualVerticalTileIndex !== null
                ? `(${virtualHorizontalTileIndex}, ${virtualVerticalTileIndex})`
                : ""}{" "}
            </button>

            <button onClick={applyDebugTile}>Apply debug tiles</button>
            <button onClick={toggleTileAnchors}>Toggle tile anchors</button>
            <button onClick={printVisibleTiles}>Print visible tiles</button>
            {visibleTiles && <div>Visible tiles: {visibleTiles}</div>}
          </div>
        )}
        <GPURequiredContainer>
          <div className={styles.panoramaViewer} ref={panoramaViewerContainerRef} />
        </GPURequiredContainer>
      </div>
    </>
  );
}

export const Route = createFileRoute("/debug/panorama-viewer")({
  component: withAuthentication(PanoramaViewerDebug, { superAdmin: true }),
});
